/**
 * Japan-time date helpers.
 *
 * The backend keys the daily home card by the JST calendar day (`app/core/jst.py`).
 * The browser clock is not that day for anyone outside Japan, and asking for the
 * wrong day misses the cache and generates a second card. Every "today" that is
 * sent to the API or compared against a cache must come from here.
 */

const JST = 'Asia/Tokyo';

// en-CA gives ISO order (YYYY-MM-DD) without hand-rolling zero padding.
const isoFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: JST,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/** @returns {string} today in Asia/Tokyo as 'YYYY-MM-DD' */
export function jstToday(now = new Date()) {
  return isoFormatter.format(now);
}

/** @returns {string} current month in Asia/Tokyo as 'YYYY-MM' */
export function jstMonth(now = new Date()) {
  return jstToday(now).slice(0, 7);
}

/** '2026-09' → '2026-08'. Null if the string is not YYYY-MM. */
export function previousYm(ym) {
  const match = /^(\d{4})-(\d{2})$/.exec(String(ym || '').trim());
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  if (month === 1) return `${year - 1}-12`;
  return `${year}-${String(month - 1).padStart(2, '0')}`;
}

/**
 * A month is complete when JST has already moved into a later month
 * (the calendar last day of `ym` has passed). No other completeness signal.
 */
export function isYmComplete(ym, now = new Date()) {
  if (!ym || !/^\d{4}-\d{2}$/.test(String(ym).trim())) return false;
  return jstMonth(now) > ym;
}

/** Most recent month whose last calendar day has passed. */
export function lastCompleteYm(now = new Date()) {
  return previousYm(jstMonth(now));
}

/** '2026-08-31' → '8/31'. Returns null for anything unparseable. */
export function formatIsoDateShort(iso) {
  if (typeof iso !== 'string') return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());
  if (!match) return null;
  return `${Number(match[2])}/${Number(match[3])}`;
}
